import type { ReactNode } from 'react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import type { BackdropProps } from '@mui/material/Backdrop';
import Drawer from '@mui/material/Drawer';
import type { DrawerProps } from '@mui/material/Drawer';
import type { ModalProps } from '@mui/material/Modal';
import type { PaperProps } from '@mui/material/Paper';
import type { SlideProps } from '@mui/material/Slide';
import type { SxProps, Theme } from '@mui/material/styles';

import showConfirmationDialog from 'components/Dialogs/ConfirmationDialog/show';
import type { ConfirmationDialogProps } from 'components/Dialogs/ConfirmationDialog/types';
import { getDrawerSx, mergeDrawerSx } from 'components/Drawers/drawerStyles';
import { STANDARD_DRAWER_WIDTHS } from 'components/Drawers/standardDrawerContract';
import type {
  DirtyConfirmationOptions,
  RootModalDrawerOptions,
  StandardDrawerCloseTransition,
  StandardDrawerPlane,
  StandardDrawerVariant,
  StandardDrawerWidth,
} from 'components/Drawers/standardDrawerContract';

// StandardDrawer wraps MUI Drawer with the app's root-modal drawer contract:
// standardized widths, right-side full-height presentation, dirty-form
// confirmation, and coordinated base/overlay animations. Temporary drawers
// behave like normal MUI temporary drawers; overlay drawers are lightweight
// surfaces used for dialogs launched from within an existing drawer.
type DrawerCloseEvent = Parameters<NonNullable<DrawerProps['onClose']>>[0];
type DrawerCloseReason = Parameters<NonNullable<DrawerProps['onClose']>>[1];
type DrawerPaperSx = PaperProps['sx'];
type DeprecatedDrawerSlotPropName = 'BackdropProps' | 'ModalProps' | 'PaperProps' | 'SlideProps';

export type StandardDrawerProps = Omit<
  DrawerProps,
  'anchor' | 'children' | 'onClose' | 'open' | DeprecatedDrawerSlotPropName | 'transitionDuration' | 'variant'
> & {
  children: ReactNode;
  closeTransition?: RootModalDrawerOptions['closeTransition'];
  dirtyConfirmation?: RootModalDrawerOptions['dirtyConfirmation'];
  isDirty?: RootModalDrawerOptions['isDirty'];
  onClose: (event?: DrawerCloseEvent, reason?: DrawerCloseReason) => void;
  onExited?: () => void;
  open: boolean;
  plane?: StandardDrawerPlane;
  showScrim?: boolean;
  variant?: StandardDrawerVariant;
  width?: StandardDrawerWidth;
  slotProps?: StandardDrawerSlotProps;
};

type StandardDrawerSlotProps = {
  backdrop?: Partial<BackdropProps>;
  root?: Partial<Omit<ModalProps, 'children' | 'onClose' | 'open'>>;
  paper?: Partial<PaperProps>;
  transition?: Partial<SlideProps>;
};

type StandardDrawerPaperSxOptions = {
  animateEnter?: boolean;
  closeTransition?: StandardDrawerCloseTransition;
  hasCoveringDrawerAbove?: boolean;
  open: boolean;
  sx?: DrawerPaperSx;
  variant: StandardDrawerVariant;
  width: StandardDrawerWidth;
};

type StandardDrawerOpenOptions = {
  open: boolean;
  shouldRender: boolean;
  temporaryDrawerOpen: boolean;
  variant: StandardDrawerVariant;
};

type StandardDrawerRootSxOptions = {
  plane?: StandardDrawerPlane;
  useTransparentBackdrop: boolean;
};

type StandardDrawerEdgeRegistration = {
  // A drawer is "covering" once it is open and has finished entering, so it now
  // hides the shadow of any drawer beneath it.
  covering: boolean;
  id: string;
};

type StandardDrawerEdgeContextValue = {
  register: (registration: StandardDrawerEdgeRegistration) => void;
  registrations: StandardDrawerEdgeRegistration[];
  unregister: (id: string) => void;
};

type StandardDrawerLayerContextValue = {
  container: HTMLElement | null;
  plane: StandardDrawerPlane;
  variant: StandardDrawerVariant;
};

export const TEMPORARY_DRAWER_TRANSITION_DURATION = 280;
export const OVERLAY_DRAWER_TRANSITION_DURATION = 200;
export const STANDARD_DRAWER_PAPER_SELECTOR = '[data-standard-drawer-paper]';
const OVERLAY_DRAWER_SLIDE_DISTANCE = 40;
const OVERLAY_DRAWER_WIDTH_INSET = 0;
const STANDARD_DRAWER_ANIMATION_EASING = 'ease';
const TEMPORARY_DRAWER_TRANSITION_DURATION_BY_PHASE = {
  enter: TEMPORARY_DRAWER_TRANSITION_DURATION,
  exit: TEMPORARY_DRAWER_TRANSITION_DURATION,
};

const StandardDrawerLayerContext = createContext<StandardDrawerLayerContextValue | null>(null);
const StandardDrawerEdgeContext = createContext<StandardDrawerEdgeContextValue | null>(null);

const getStandardDrawerCloseTransform = (closeTransition: StandardDrawerCloseTransition): string =>
  closeTransition === 'fullSlideFade' ? 'translateX(100%)' : `translateX(${OVERLAY_DRAWER_SLIDE_DISTANCE}px)`;

// Menus, popovers, and poppers owned by drawer content portal into their owning
// drawer root, so this modal-level z-index resolves *inside* that root's
// stacking context rather than against the page. It therefore lifts floating UI
// above its own drawer's paper without letting it escape above a later drawer,
// and it stays correct whichever plane the drawer stack itself occupies.
export const getStandardDrawerFloatingSx = (): SxProps<Theme> => {
  const zIndex = (theme: Theme) => theme.zIndex.modal;
  const importantZIndex = (theme: Theme) => `${theme.zIndex.modal} !important`;
  return {
    zIndex,
    '&&&': {
      zIndex: importantZIndex,
    },
  };
};

// Drawers normally own the drawer plane. A drawer opened after a true modal
// joins the modal plane instead, so it paints above the dialog that opened it
// rather than under that dialog's backdrop. The plane is resolved per drawer, so
// one visual stack can span both planes when a modal is opened partway through
// it. The modal plane is used exactly, not offset, so DOM order still decides
// between same-plane surfaces: a confirmation opened from the drawer mounts later
// and stays on top.
//
// Within a plane, drawers mounted later are painted above earlier ones by DOM
// order. MUI ModalManager sees that same mount order, which is what keeps focus
// and aria-hidden aligned with the visible top layer. No drawer computes a
// per-instance z-index.
export const getStandardDrawerRootSx = ({
  plane = 'drawer',
  useTransparentBackdrop,
}: StandardDrawerRootSxOptions): SxProps<Theme> => ({
  zIndex: (theme) => (plane === 'modal' ? theme.zIndex.modal : theme.zIndex.drawer),
  ...(useTransparentBackdrop ? { '& .MuiBackdrop-root': { backgroundColor: 'transparent' } } : {}),
});

// Each drawer keeps its own shadow. A drawer drops its shadow only once a drawer
// above it (later in the stack) is fully entered and covering it, so stacked
// same-width drawers do not paint a doubled, darkened divider and the lower
// shadow stays visible throughout the upper drawer's enter animation.
// Registration order mirrors mount order, so "above" means later in the array.
export const hasCoveringDrawerAbove = (registrations: StandardDrawerEdgeRegistration[], id: string): boolean => {
  const index = registrations.findIndex((registration) => registration.id === id);
  return index !== -1 && registrations.slice(index + 1).some((registration) => registration.covering);
};

export function StandardDrawerEdgeProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [registrations, setRegistrations] = useState<StandardDrawerEdgeRegistration[]>([]);
  // Upsert in place so a covering-flag update never reorders the stack; only a
  // fresh mount appends, preserving mount order for "above" comparisons.
  const register = useCallback((registration: StandardDrawerEdgeRegistration): void => {
    setRegistrations((current) => {
      const index = current.findIndex(({ id }) => id === registration.id);
      if (index === -1) {
        return [...current, registration];
      }
      if (current[index].covering === registration.covering) {
        return current;
      }
      const next = [...current];
      next[index] = registration;
      return next;
    });
  }, []);
  const unregister = useCallback((id: string): void => {
    setRegistrations((current) => current.filter((registration) => registration.id !== id));
  }, []);
  const contextValue = useMemo<StandardDrawerEdgeContextValue>(
    () => ({ register, registrations, unregister }),
    [register, registrations, unregister],
  );

  return <StandardDrawerEdgeContext.Provider value={contextValue}>{children}</StandardDrawerEdgeContext.Provider>;
}

export const useStandardDrawerVariant = (): StandardDrawerVariant | null =>
  useContext(StandardDrawerLayerContext)?.variant ?? null;

export const useStandardDrawerLayerContainer = (): HTMLElement | null =>
  useContext(StandardDrawerLayerContext)?.container ?? null;

export const getStandardDrawerPaper = (element: Element | null | undefined): Element | null =>
  element?.closest(STANDARD_DRAWER_PAPER_SELECTOR) ?? null;

export function StandardDrawerLayerProvider({
  children,
  container = null,
  plane,
  variant,
}: {
  children?: ReactNode;
  container?: HTMLElement | null;
  plane?: StandardDrawerPlane;
  variant?: StandardDrawerVariant;
}): React.JSX.Element {
  const parentLayerContext = useContext(StandardDrawerLayerContext);
  const layerContextValue = useMemo<StandardDrawerLayerContextValue>(
    () => ({
      container: container ?? parentLayerContext?.container ?? null,
      plane: plane ?? parentLayerContext?.plane ?? 'drawer',
      variant: variant ?? parentLayerContext?.variant ?? 'temporary',
    }),
    [container, parentLayerContext, plane, variant],
  );

  return (
    <StandardDrawerLayerContext.Provider value={layerContextValue}>{children}</StandardDrawerLayerContext.Provider>
  );
}

// A true modal rendered from drawer content portals to document.body above the
// drawer, so floating UI inside it must not portal back into that drawer's root
// and paint under the modal. Wrap the modal's content to clear the drawer layer.
export function StandardDrawerLayerReset({ children }: { children?: ReactNode }): React.JSX.Element {
  return <StandardDrawerLayerContext.Provider value={null}>{children}</StandardDrawerLayerContext.Provider>;
}

export const getStandardDrawerTransitionDuration = (variant: StandardDrawerVariant): number =>
  variant === 'temporary' ? TEMPORARY_DRAWER_TRANSITION_DURATION : OVERLAY_DRAWER_TRANSITION_DURATION;

export const shouldUseTransparentStandardDrawerBackdrop = ({
  hideBackdrop,
  showScrim,
}: {
  hideBackdrop?: boolean;
  showScrim: boolean;
}): boolean => !showScrim && !hideBackdrop;

export const getStandardDrawerOpen = ({
  open,
  shouldRender,
  temporaryDrawerOpen,
  variant,
}: StandardDrawerOpenOptions): boolean => (variant === 'overlay' ? shouldRender : open && temporaryDrawerOpen);

const getSxArray = (sx: SxProps<Theme> | undefined): SxProps<Theme>[] => {
  if (!sx) {
    return [];
  }
  return Array.isArray(sx) ? sx : [sx];
};

export const getStandardDrawerPaperSx = ({
  animateEnter = true,
  closeTransition,
  hasCoveringDrawerAbove = false,
  open,
  sx,
  variant,
  width,
}: StandardDrawerPaperSxOptions): DrawerPaperSx => {
  const closeTransform = getStandardDrawerCloseTransform(closeTransition ?? 'shortSlideFade');
  const drawerWidth = STANDARD_DRAWER_WIDTHS[width];
  const shouldAnimateOverlay = variant === 'overlay' && (!open || animateEnter);
  // A drawer covered by a fully entered drawer above it drops its shadow so
  // overlapping shadows do not darken the shared edge.
  const baseSx: SxProps<Theme> = {
    boxSizing: 'border-box',
    boxShadow: hasCoveringDrawerAbove ? 'none' : (theme) => theme.shadows[24],
    height: '100%',
    maxHeight: '100%',
    // Resolved inside the drawer root's own stacking context, so this only ranks
    // the paper against its sibling backdrop -- it is not the plane the stack
    // occupies. A stack raised to the modal plane still carries this value; see
    // getStandardDrawerRootSx for the plane that actually positions the drawer.
    zIndex: (theme) => theme.zIndex.drawer,
    width: {
      xs: '100vw',
      sm: drawerWidth,
    },
  };

  // Temporary drawers use MUI's Slide transition. Overlay drawers animate the
  // paper itself so they can fade and move only 40px while sitting above the
  // current drawer instead of replaying the full-width slide.
  const overlaySx: SxProps<Theme> =
    variant === 'overlay'
      ? {
          width: {
            xs: `calc(100vw - ${OVERLAY_DRAWER_WIDTH_INSET}px)`,
            sm:
              typeof drawerWidth === 'number'
                ? drawerWidth - OVERLAY_DRAWER_WIDTH_INSET
                : `calc(${drawerWidth} - ${OVERLAY_DRAWER_WIDTH_INSET}px)`,
          },
          '@keyframes standard-overlay-drawer-enter': {
            from: {
              opacity: 0,
              transform: `translateX(${OVERLAY_DRAWER_SLIDE_DISTANCE}px)`,
            },
            to: {
              opacity: 1,
              transform: 'translateX(0)',
            },
          },
          '@keyframes standard-overlay-drawer-exit': {
            from: {
              opacity: 1,
              transform: 'translateX(0)',
            },
            to: {
              opacity: 0,
              transform: closeTransform,
            },
          },
          ...(shouldAnimateOverlay
            ? {
                animation: `${
                  open ? 'standard-overlay-drawer-enter' : 'standard-overlay-drawer-exit'
                } ${OVERLAY_DRAWER_TRANSITION_DURATION}ms ${STANDARD_DRAWER_ANIMATION_EASING} forwards`,
              }
            : {}),
        }
      : {};

  return [baseSx, overlaySx, ...getSxArray(sx as SxProps<Theme>)] as DrawerPaperSx;
};

const getDirtyConfirmationProps = (
  onConfirm: () => void,
  dirtyConfirmation?: DirtyConfirmationOptions,
): ConfirmationDialogProps => ({
  title: dirtyConfirmation?.title ?? 'Discard changes?',
  content:
    dirtyConfirmation?.content ??
    'You have unsaved changes. Are you sure you want to discard them and close this drawer?',
  confirmLabel: dirtyConfirmation?.confirmLabel ?? 'Discard',
  denyLabel: dirtyConfirmation?.denyLabel ?? 'Keep editing',
  confirmAction: onConfirm,
});

export default function StandardDrawer(props: StandardDrawerProps) {
  const {
    children,
    closeTransition = 'shortSlideFade',
    dirtyConfirmation,
    hideBackdrop: hideBackdropProp,
    isDirty = false,
    onClose,
    onExited,
    open,
    plane: planeProp,
    showScrim = true,
    slotProps,
    sx,
    width = 'Medium',
    variant = 'temporary',
    ...otherProps
  } = props;
  const parentLayerContext = useContext(StandardDrawerLayerContext);
  const plane = planeProp ?? parentLayerContext?.plane ?? 'drawer';
  const duration = getStandardDrawerTransitionDuration(variant);
  const [shouldRender, setShouldRender] = useState(open);
  const [temporaryDrawerOpen, setTemporaryDrawerOpen] = useState(false);
  const [overlayEnterComplete, setOverlayEnterComplete] = useState(false);
  const [drawerLayerContainer, setDrawerLayerContainer] = useState<HTMLElement | null>(null);
  const edgeContext = useContext(StandardDrawerEdgeContext);
  const registrations = edgeContext?.registrations;
  const registerEdge = edgeContext?.register;
  const unregisterEdge = edgeContext?.unregister;
  const edgeRegistrationId = useId();
  // This drawer covers the ones below it once it is open and fully entered.
  // Temporary drawers are always the base layer, so their frame is enough.
  const isCovering = shouldRender && (variant === 'overlay' ? overlayEnterComplete : open && temporaryDrawerOpen);
  // Drop this drawer's shadow only once a drawer above it is covering it, so the
  // lower shadow stays visible for the whole enter animation of the upper drawer.
  const isCoveredFromAbove = registrations ? hasCoveringDrawerAbove(registrations, edgeRegistrationId) : false;
  // Drawers without a scrim keep a transparent backdrop mounted so clicks stay
  // isolated to the top drawer instead of reaching lower stacked surfaces.
  const hideBackdrop = hideBackdropProp;
  const useTransparentBackdrop = shouldUseTransparentStandardDrawerBackdrop({ hideBackdrop, showScrim });

  // Register presence and clean up on unmount; covering changes are synced
  // separately so they update in place instead of reordering the stack.
  useLayoutEffect(() => {
    if (!registerEdge || !unregisterEdge || !shouldRender) {
      return undefined;
    }

    registerEdge({ id: edgeRegistrationId, covering: false });
    return () => unregisterEdge(edgeRegistrationId);
  }, [edgeRegistrationId, registerEdge, shouldRender, unregisterEdge]);

  useLayoutEffect(() => {
    if (!registerEdge || !shouldRender) {
      return;
    }

    registerEdge({ id: edgeRegistrationId, covering: isCovering });
  }, [edgeRegistrationId, isCovering, registerEdge, shouldRender]);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      return undefined;
    }

    if (variant === 'temporary' || !shouldRender) {
      return undefined;
    }

    // MUI's Slide is disabled for overlay drawers, so keep the drawer mounted
    // until the CSS fade/translate animation has had time to finish.
    const timeoutId = window.setTimeout(() => {
      setShouldRender(false);
      onExited?.();
    }, duration);

    return () => window.clearTimeout(timeoutId);
  }, [duration, onExited, open, shouldRender, variant]);

  useEffect(() => {
    if (variant !== 'temporary') {
      return undefined;
    }

    if (!open || !shouldRender) {
      setTemporaryDrawerOpen(false);
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      setTemporaryDrawerOpen(true);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [open, shouldRender, variant]);

  useEffect(() => {
    if (variant !== 'overlay') {
      return undefined;
    }

    if (!open || !shouldRender) {
      setOverlayEnterComplete(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setOverlayEnterComplete(true);
    }, duration);

    return () => window.clearTimeout(timeoutId);
  }, [duration, open, shouldRender, variant]);

  const handleExited: NonNullable<SlideProps['onExited']> = useCallback(
    (node) => {
      setShouldRender(false);
      onExited?.();
      slotProps?.transition?.onExited?.(node);
    },
    [onExited, slotProps?.transition],
  );

  const handleClose = useCallback<NonNullable<DrawerProps['onClose']>>(
    (event, reason) => {
      // Only background clicks are guarded. Explicit close actions should keep
      // using their dialog-owned confirmation/validation flows.
      if (reason === 'backdropClick' && isDirty) {
        showConfirmationDialog(
          getDirtyConfirmationProps(() => {
            onClose(undefined, reason);
          }, dirtyConfirmation),
        );
        return;
      }

      onClose(event, reason);
    },
    [dirtyConfirmation, isDirty, onClose],
  );

  // Overlay drawer visibility is controlled by shouldRender so the exit CSS
  // animation can run after callers flip open to false. Temporary drawers mount
  // closed first, then open on the next animation frame so MUI Slide can run
  // its enter transition instead of appearing at the final position.
  const drawerOpen = getStandardDrawerOpen({ open, shouldRender, temporaryDrawerOpen, variant });
  const transitionSlotProps = useMemo<Partial<SlideProps>>(() => {
    const resolvedTransitionSlotProps: Partial<SlideProps> = {
      ...slotProps?.transition,
      onExited: variant === 'temporary' ? handleExited : slotProps?.transition?.onExited,
    };

    if (variant === 'temporary' && resolvedTransitionSlotProps.easing === undefined) {
      resolvedTransitionSlotProps.easing = STANDARD_DRAWER_ANIMATION_EASING;
    }

    return resolvedTransitionSlotProps;
  }, [handleExited, slotProps?.transition, variant]);

  const paperSx = useMemo(
    () =>
      getStandardDrawerPaperSx({
        animateEnter: !overlayEnterComplete,
        closeTransition,
        hasCoveringDrawerAbove: isCoveredFromAbove,
        open,
        sx: slotProps?.paper?.sx,
        variant,
        width,
      }),
    [slotProps?.paper?.sx, closeTransition, isCoveredFromAbove, open, overlayEnterComplete, variant, width],
  );
  const drawerSlotProps = useMemo<DrawerProps['slotProps']>(
    () => ({
      ...slotProps,
      backdrop: slotProps?.backdrop,
      root: {
        ...slotProps?.root,
        keepMounted: false,
      },
      paper: {
        ...slotProps?.paper,
        'data-standard-drawer-paper': true,
        sx: paperSx,
      },
      transition: transitionSlotProps,
    }),
    [paperSx, slotProps, transitionSlotProps],
  );

  if (!shouldRender) {
    return null;
  }

  return (
    <Drawer
      anchor='right'
      hideBackdrop={hideBackdrop}
      onClose={handleClose}
      open={drawerOpen}
      ref={setDrawerLayerContainer}
      sharedcomponentid='StandardDrawer'
      slotProps={drawerSlotProps}
      sx={getDrawerSx(mergeDrawerSx(getStandardDrawerRootSx({ plane, useTransparentBackdrop }), sx))}
      transitionDuration={variant === 'temporary' ? TEMPORARY_DRAWER_TRANSITION_DURATION_BY_PHASE : 0}
      variant='temporary'
      {...otherProps}
    >
      <StandardDrawerLayerProvider container={drawerLayerContainer} plane={plane} variant={variant}>
        {children}
      </StandardDrawerLayerProvider>
    </Drawer>
  );
}
